import React from 'react';
import './DatePicker.css';
import 'react-datepicker/dist/react-datepicker.css';
import { useFilterLogicV2 } from '@hooks/useFilterLogicV2';
import useBreakpoint from '@hooks/useBreakpoint';
import {
  FILTER_ORDER_V2,
  createStaticOptionsFunction,
  isFilterVisible,
  isFilterDisabled,
  getFilterLabel,
  getSelectedValue,
  getParentFilters,
  shouldShowSearch,
} from './utils/filterAdapterV2';
import PsdFilter from '@v2/components/PsdFilter';

const FilterBarV2 = () => {
  const filterLogic = useFilterLogicV2();
  const breakpoint = useBreakpoint();

  // Determine menu position based on breakpoint
  const menuPosition =
    breakpoint === 'desktop' ? 'bottom-right' : 'bottom-left';

  /**
   * Handle filter value changes from PsdFilter
   * This syncs the changed value to the legacy filter context
   */
  const handleFilterChange = (value, filterId) => {
    // Find the full option object from the options array using integer ID
    const getFullOption = (options, value, idField) => {
      return options.find((opt) => {
        // Prioritize the specific ID field for the filter type
        const optValue = opt[idField] ?? opt.id ?? opt.value;
        return optValue === value;
      });
    };

    // Sync to legacy context based on filter type
    switch (filterId) {
      case 'program': {
        const program = getFullOption(
          filterLogic.programOptions,
          value,
          'programId'
        );
        if (program) {
          filterLogic.setSelectedProgram(program);
        }
        break;
      }
      case 'product': {
        const product = getFullOption(
          filterLogic.productOptions,
          value,
          'productId'
        );
        if (product) {
          filterLogic.setSelectedProduct(product);
        }
        break;
      }
      case 'model': {
        const model = getFullOption(filterLogic.modelOptions, value, 'id');
        if (model) {
          filterLogic.setSelectedModel(model);
        }
        break;
      }
      case 'sector': {
        const sector = getFullOption(
          filterLogic.sectorOptions,
          value,
          'sectorId'
        );
        if (sector) {
          filterLogic.updateSectorOption(sector);
        }
        break;
      }
      case 'market': {
        const market = getFullOption(
          filterLogic.marketOptions,
          value,
          'marketId'
        );
        if (market) {
          filterLogic.setSelectedMarket(market);
        }
        break;
      }
      case 'site': {
        const plant = getFullOption(filterLogic.plantOptions, value, 'plantId');
        if (plant) {
          filterLogic.setSelectedPlant(plant);
        }
        break;
      }
      case 'customer': {
        const customer = getFullOption(
          filterLogic.customerOptions,
          value,
          'id'
        );
        if (customer) {
          filterLogic.setSelectedCustomer(customer);
        }
        break;
      }
      default:
        break;
    }
  };

  /**
   * Renders a PsdFilter based on filter key and configuration
   */
  const renderFilter = (filterKey) => {
    // Check if filter should be visible
    const isVisible = isFilterVisible(filterKey, filterLogic);
    if (!isVisible) {
      return null;
    }

    // Get filter properties
    const label = getFilterLabel(filterKey, filterLogic);
    const selectedValue = getSelectedValue(filterKey, filterLogic);
    const disabled = isFilterDisabled(filterKey, filterLogic);
    const parentFilters = getParentFilters(filterKey, filterLogic);
    const showSearch = shouldShowSearch(filterKey);
    const staticOptionsFunction = createStaticOptionsFunction(
      filterKey,
      filterLogic
    );

    return (
      <PsdFilter
        key={filterKey}
        filterId={filterKey}
        label={label}
        staticOptionsFunction={staticOptionsFunction}
        parentFilterArray={parentFilters}
        enabled={!disabled}
        selectedValue={selectedValue}
        menuPosition={menuPosition}
        showSearch={showSearch}
        onChange={handleFilterChange}
      />
    );
  };

  // Unified view with PsdFilter components for all screen sizes
  return <>{FILTER_ORDER_V2.map(renderFilter)}</>;
};

export default FilterBarV2;
